import React, { FC } from 'react'
import { TFunction } from 'next-i18next'
import styled from 'styled-components'
import Octicon, { Check, X, Gear } from '@primer/octicons-react'
import cn from 'classnames'
import { dateFormatter } from '../../utils/formatters'
import moment from 'moment'
import styles from '~/styles/orderHistory.module.css'

interface Props {
  t: TFunction
  history
  locale: string
}

const StyledTh = styled.th`
  color: #4b5563;
  font-size: 12px;
  text-align: left;
  font-weight: 500;
`

const OrderHistory: FC<Props> = ({ t, history, locale, hint, timezone, isSyntheticDealer, order }) => {
	let coloredStatuses = ['APPROVED', 'PLACED_IN_POSTAMAT', 'PICKED_UP', 'CANCELLED', 'IN_PROGRESS']
			coloredStatuses.push('CREATED')
			coloredStatuses.push('UNCLAIMED')
	  	coloredStatuses.push('EXPIRATION')
	  	coloredStatuses.push('PROBLEM')
	  	coloredStatuses.push('REJECTED')
	  	coloredStatuses.push('RETURNED')
	 const isCustomerSaidPickedUp = order.picked_up === order.delivery_date

  return (
    <div className="border-t border-gray-200 mt-6">
      <h3 className="font-bold mt-4 mb-2">
      	{t('Orders:Order history') + ' (UTC' + (timezone >= 0 ? '+' : '') + timezone + ')'}
      </h3>
      <table className="w-full">
        <thead>
          <tr>
            <StyledTh className="p-2">{t('Orders:Date')}</StyledTh>
            <StyledTh>{t('Orders:Status')}</StyledTh>
            <StyledTh>{t('Paid')}</StyledTh>
          </tr>
        </thead>
        <tbody>
          {history &&
            history.map((entry, i) => {
            	return (
								<React.Fragment key={i}>
									{entry.type !== 'notification' ? (
										<tr 
											key={i}
											className={'hover:bg-gray-100'}
										>
											<td className="px-2 pt-4 pb-0">
												<span className="text-gray-600">
													{dateFormatter(entry.date, locale, 0, 0, true)}
												</span>
											</td>
											<td className="px-2 pt-4 pb-0">
												<span
													className={cn(
													  'text-sm font-bold px-2 py-1 rounded text-center',
														{
															[styles.receivedByViaD]: entry.status === 'CREATED',
														  [styles.approvedStatus]: entry.status === 'APPROVED',
															[styles.inProgressStatus]: entry.status === 'IN_PROGRESS',
															[isCustomerSaidPickedUp ? styles.customerSaidPickedUp : styles.pickedUpStatus]: entry.status === 'PICKED_UP',
															[styles.expirationStatus]: entry.status === 'EXPIRATION',
															[styles.returnedStatus]: entry.status === 'RETURNED',
														  'bg-indigo-200 text-indigo-700': entry.status === 'PLACED_IN_POSTAMAT',
														  'bg-orange-200 text-orange-700': entry.status === 'EXPIRING',
														  'bg-red-200 text-red-700': [ 'UNCLAIMED', 'PROBLEM', 'REJECTED' ].includes(entry.status),
														  'bg-gray-300 text-gray-800': entry.status === 'CANCELLED',
															'bg-gray-200 text-gray-700': !(coloredStatuses.includes(entry.status))
														},
													)}
												>
													{t(`TEMPStatuses:${entry.status}`)}
												</span>
											</td>
											<td className="px-2 pt-4 pb-0">
												<span>
													{entry.paid ? (
														<Octicon icon={Check} className="text-green-400" />
													) : (
														<Octicon icon={X} className="text-red-400" />
													)}
												</span>
											</td>
										</tr>
									) : null}
									{entry.type === 'notification' ? (
										<tr
											key={entry.type + i}
											id={'OrderHistory_' + entry.type + i}
											className={styles[entry.type + 'Value'] + ' hover:bg-gray-100'}
											onMouseEnter={onMouseEnterNotif.bind(this, 'OrderHistory_' + entry.type + i, entry, locale, hint, timezone, t)}
											onMouseLeave={onMouseLeaveNotif.bind(this, hint)}
										>
											<td
												className="px-2 pb-0"
												colSpan="3"
											>
												<span>
													{entry.messageType && !(entry.messageType === 'Error' && (entry.recipient + '').includes('errorMessage')) ? entry.messageType + ':' : ''} 
												</span>
												<span>
													{recipientToString(entry.recipient, entry.status)}
												</span>
											</td>
										</tr>
									) : null}
							</React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const recipientToString = (recipient: string, status: string): JSX.Element => {
	if ((recipient + '').includes('errorMessage')) {
			try {
				let data = JSON.parse(recipient)
				let message = JSON.parse(data.errorMessage)
				let errorDetails = JSON.stringify(message, null, 2)
				if (message?.Notifications?.[0]?.Message) {
					return (
						<span>
							{"Error: " + message?.Notifications?.[0]?.Message}
						</span>
					)
				} else {
					return (
						<div className={styles.lastError}><pre>{errorDetails}</pre></div>
					)
				}
			} catch (e) {
				return recipientToPhoneNumber(recipient) 
			}
	} else {
		if (status === 'NEW') {
			return recipient
		}
		return recipientToPhoneNumber(recipient)
	}
}

const recipientToPhoneNumber = (recipient: string): JSX.Element => {
	let ret = recipient
	let isPhoneNumber = false
	const regExp = /^([\d]{1,})([\d]{3})([\d]{3})([\d]{2})([\d]{2})$/
	if (typeof +(recipient) === 'number' && (regExp).test(recipient + '')) {
		ret = (recipient + '').replace(regExp, '+$1 $2 $3-$4$5')
		isPhoneNumber = true
	}
	if (isPhoneNumber) {
		return (
			<nobr>
				{ret}
			</nobr>
		)
	} else {
		return (
			<span>
				{ret}
			</span>
		)
	}
}

const onMouseEnterNotif = (id, entry, locale, hint, timezone, t): void => {
	if (entry.messageType !== 'Error') {
		const sentValue = dateFormatter(entry.sentDate, locale, 0, 0, true)
		let hintValue
		let showHint = true
		if (entry.recipient === 'client`s label') {
			showHint = false
		}
		if (showHint) {
			if (entry.opened_at?.length) {
				hintValue = []
				hintValue.push((
					<>
						<div
							className={styles.hintHeader}
						>
							{t('Sent to customer at') + ': '}
						</div>
						<div
							className={styles.hintValue}
						>
							{sentValue}
						</div>
						<div
							className={styles.hintHeader}
						>
							{t('Opened by customer at') + ': '}
						</div>
					</>
				))
				const isLast = (entry.opened_at?.length === 1)
				hintValue.push(getOpenedEmailDate(entry.opened_at[0], locale, timezone, isLast, t))
				if (entry.opened_at?.length > 1) {
					if (entry.opened_at?.length === 3) {
						hintValue.push(getOpenedEmailDate(entry.opened_at[1], locale, timezone, false, t))
					}
					if (entry.opened_at?.length > 3) {
						hintValue.push(getOpenedEmailDate(null, locale, timezone, false, t, entry.opened_at?.length - 2))
					}
					if (entry.opened_at?.length > 1) {
						const last = entry.opened_at?.length - 1
						hintValue.push(getOpenedEmailDate(entry.opened_at[last], locale, timezone, true, t))
					}
				}
			} else {
				hintValue = (
					<>
						<div
							className={styles.hintHeader}
						>
							{t('Sent to customer at') + ': '}
						</div>
						<div
							className={styles.hintValue}
						>
							{sentValue}
						</div>
					</>
				)
			}
			const element = document.body.querySelector('#' + id)
			if (element) {
				const bodyRect = document.body.getBoundingClientRect()
				const rect = element.getBoundingClientRect()
				hint?.setHint({
					value: hintValue,
					top: rect.top - bodyRect.top,
					left: rect.right,
					isOpenedDates: entry.opened_at?.length,
				})
			}
		}
	}
}

const onMouseLeaveNotif = (hint): void => {
	hint?.setHint({})
}

const getOpenedEmailDate = (date, locale, timezone, isLast, t, count): JSX.Element => {
	let value = dateFormatter(date, locale, 0, 0, true)
	if (count) {
		value = count + ' ' + (count > 1 ? t('openedTimes') : t('openedOneTime'))
	}
	return (
		<div
			className={styles.hintValue}
		>
			{value + (isLast ? '.' : ',')}
		</div>
	)
}

export default OrderHistory